/**
 * Sprint 11+12 — Keyboard shortcut help.
 * Two-column reference rendered when the user presses `?`.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useUIStore } from '@/stores/ui'
import { Kbd } from './Kbd'
import { NAV_SHORTCUTS, NEW_SHORTCUTS } from '@/hooks/useKeyboardShortcuts'

export function ShortcutsModal() {
  const open = useUIStore((s) => s.shortcutsHelpOpen)
  const close = useUIStore((s) => s.closeShortcutsHelp)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={close}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--card-bg)] border border-[var(--border-themed)] text-[var(--text-primary)]"
        style={{
          borderRadius: 16, width: 720, maxWidth: '95vw', maxHeight: '85vh',
          overflowY: 'auto', boxShadow: '0 16px 36px rgba(15,23,42,0.18)',
        }}
      >
        <div
          className="flex items-center justify-between"
          style={{ padding: '18px 22px', borderBottom: '1px solid var(--border-themed)' }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Keyboard shortcuts</h2>
            <div className="text-[var(--text-secondary)]" style={{ marginTop: 2, fontSize: 12 }}>
              Press <KbdInline>?</KbdInline> anytime to show this panel.
            </div>
          </div>
          <button
            onClick={close}
            aria-label="Close"
            className="p-1 rounded-md hover:bg-[var(--bg-tertiary)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-6 grid-cols-1 md:grid-cols-2" style={{ padding: 22 }}>
          <Section title="Navigation" subtitle="Press G then…">
            {NAV_SHORTCUTS.map((s) => (
              <Row key={s.key} label={s.defaultLabel}>
                <Kbd>G</Kbd>
                <span className="text-[11px] text-[var(--text-muted-themed)]">then</span>
                <Kbd>{s.key.toUpperCase()}</Kbd>
              </Row>
            ))}
          </Section>

          <Section title="Global actions">
            {NEW_SHORTCUTS.map((s) => (
              <Row key={s.key} label={s.defaultLabel}><Kbd>{s.key.toUpperCase()}</Kbd></Row>
            ))}
            <Row label="Open command palette">
              <Kbd>⌘</Kbd><span className="text-[11px] text-[var(--text-muted-themed)]">+</span><Kbd>K</Kbd>
            </Row>
            <Row label="Focus global search"><Kbd>/</Kbd></Row>
            <Row label="This help panel"><Kbd>?</Kbd></Row>
            <Row label="Close any panel"><Kbd>Esc</Kbd></Row>
          </Section>
        </div>

        <div
          className="text-[11px] text-[var(--text-muted-themed)]"
          style={{ padding: '10px 22px 18px', textAlign: 'center', borderTop: '1px solid var(--border-themed)' }}
        >
          Shortcuts are disabled while you're typing in a field.
        </div>
      </div>
    </div>,
    document.body,
  )
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
        {subtitle ? (
          <div className="text-[var(--text-muted-themed)]" style={{ fontSize: 11, marginTop: 2 }}>{subtitle}</div>
        ) : null}
      </div>
      <div>{children}</div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '7px 0',
        borderBottom: '1px solid var(--border-themed)',
      }}
    >
      <span className="text-[var(--text-secondary)]" style={{ fontSize: 13 }}>{label}</span>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{children}</div>
    </div>
  )
}

function KbdInline({ children }: { children: React.ReactNode }) {
  return <Kbd className="align-middle">{children}</Kbd>
}
